<template>
  <div class="acne-animations" style="display:none">
    <!-- This is a functional component that has no visual output -->
  </div>
</template>

<script>
class RetinASparkles {
    constructor(canvas) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');
        this.sparkles = [];
        this.centerX = canvas.width / 2;
        this.centerY = canvas.height / 2;
        this.startY = canvas.height * 0.35; // Start 20% higher than center (0.5 - 0.2 = 0.3)
        this.maxRadius = Math.min(canvas.width, canvas.height) / 2;
        this.isAnimating = true;
        this.init();
    }

    init() {
        // Set canvas size to match container
        const resizeCanvas = () => {
            const container = this.canvas.parentElement;
            this.canvas.width = container.offsetWidth;
            this.canvas.height = container.offsetHeight;
            this.centerX = this.canvas.width / 2;
            this.centerY = this.canvas.height / 2;
            this.startY = this.canvas.height * 0.3; // Update start position on resize
            this.maxRadius = Math.min(this.canvas.width, this.canvas.height) / 2;
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        // Create initial burst of sparkles
        for (let i = 0; i < 50; i++) {
            this.createSparkle();
        }

        this.animate();
    }

    createSparkle() {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 2.5 + 1;
        const size = Math.random() * 4 + 3;
        
        this.sparkles.push({
            x: this.centerX,
            y: this.startY, // Use startY instead of centerY
            angle: angle,
            speed: speed,
            size: size,
            distance: 0,
            opacity: 1,
            color: this.getRandomBlueColor(),
            rotation: Math.random() * Math.PI * 2,
            dropSpeed: Math.random() * 1.5 + 0.5 // Random drop speed for each star
        });
    }

    getRandomBlueColor() {
        const colors = [
            '#ADD8E6', // Light Blue
            '#87CEEB', // Sky Blue
            '#B0E0E6', // Powder Blue
            '#E0FFFF'  // Light Cyan
        ];
        return colors[Math.floor(Math.random() * colors.length)];
    }

    drawStar(x, y, size, rotation) {
        const spikes = 4;
        const outerRadius = size;
        const innerRadius = size / 2;

        // Draw outer glow
        this.ctx.beginPath();
        this.ctx.moveTo(x, y - outerRadius * 1.2);
        for (let i = 0; i < spikes * 2; i++) {
            const radius = i % 2 === 0 ? outerRadius * 1.2 : innerRadius * 1.2;
            const angle = (Math.PI / spikes) * i + rotation;
            const pointX = x + Math.sin(angle) * radius;
            const pointY = y - Math.cos(angle) * radius;
            this.ctx.lineTo(pointX, pointY);
        }
        this.ctx.closePath();
        this.ctx.fillStyle = this.hexToRgba('#FFFFFF', 0.3);
        this.ctx.fill();

        // Draw middle glow
        this.ctx.beginPath();
        this.ctx.moveTo(x, y - outerRadius * 1.1);
        for (let i = 0; i < spikes * 2; i++) {
            const radius = i % 2 === 0 ? outerRadius * 1.1 : innerRadius * 1.1;
            const angle = (Math.PI / spikes) * i + rotation;
            const pointX = x + Math.sin(angle) * radius;
            const pointY = y - Math.cos(angle) * radius;
            this.ctx.lineTo(pointX, pointY);
        }
        this.ctx.closePath();
        this.ctx.fillStyle = this.hexToRgba('#FFFFFF', 0.5);
        this.ctx.fill();

        // Draw main star
        this.ctx.beginPath();
        this.ctx.moveTo(x, y - outerRadius);
        for (let i = 0; i < spikes * 2; i++) {
            const radius = i % 2 === 0 ? outerRadius : innerRadius;
            const angle = (Math.PI / spikes) * i + rotation;
            const pointX = x + Math.sin(angle) * radius;
            const pointY = y - Math.cos(angle) * radius;
            this.ctx.lineTo(pointX, pointY);
        }
        this.ctx.closePath();
    }

    animate() {
        if (!this.isAnimating) return;

        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        
        // Update and draw sparkles
        this.sparkles = this.sparkles.filter(sparkle => {
            sparkle.distance += sparkle.speed;
            // Calculate horizontal and vertical position with drop effect
            const horizontalDistance = Math.cos(sparkle.angle) * sparkle.distance;
            const verticalDistance = Math.sin(sparkle.angle) * sparkle.distance;
            const dropDistance = sparkle.dropSpeed * (sparkle.distance / this.maxRadius) * 50;
            
            sparkle.x = this.centerX + horizontalDistance;
            // Use startY as the base position for vertical movement
            sparkle.y = this.startY + verticalDistance + dropDistance;
            sparkle.opacity = 1 - (sparkle.distance / this.maxRadius);
            sparkle.rotation += 0.05;

            if (sparkle.distance < this.maxRadius) {
                this.drawStar(sparkle.x, sparkle.y, sparkle.size, sparkle.rotation);
                this.ctx.fillStyle = this.hexToRgba(sparkle.color, sparkle.opacity);
                this.ctx.fill();
                return true;
            }
            return false;
        });

        // Stop animation if all sparkles are gone
        if (this.sparkles.length === 0) {
            this.isAnimating = false;
            this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
            return;
        }

        requestAnimationFrame(() => this.animate());
    }

    hexToRgba(hex, alpha) {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
}

export default {
  name: 'AcneAnimations',
  
  mounted() {
    this.$nextTick(() => {
      this.triggerAnimations();
      this.observePills();
    });
  },
  
  methods: {
    observePills() {
      // Select only pills containers that are NOT inside medications-grid
      const pillsContainers = document.querySelectorAll('.medication-item__pills:not(.medications-grid .medication-item__pills)');
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const pills = entry.target.querySelectorAll('.medication-item__pill');
            pills.forEach((pill, index) => {
              pill.classList.add('animate-pill');
            });
          }
        });
      }, {
        threshold: 0.2 // Trigger when 20% of the element is visible
      });

      pillsContainers.forEach(container => {
        observer.observe(container);
      });
    },

    triggerAnimations() {
      setTimeout(() => {
        const sectionTitles = document.querySelectorAll('.section-title');
        sectionTitles.forEach(title => {
          title.classList.add('animate');
        });
        
        const medicationItems = document.querySelectorAll('.medication-item');
        medicationItems.forEach(item => {
          item.classList.add('animate');
        });
        
        const dermatologElements = document.querySelectorAll('.dermatolog');
        dermatologElements.forEach(elem => {
          if (elem.parentElement && elem.parentElement.classList.contains('section-title')) {
            elem.parentElement.classList.add('animate');
          } else {
            elem.classList.add('animate');
          }
        });

        setTimeout(() => {
          const retinACanvases = document.querySelectorAll('.retin-a-sparkles .sparkles-canvas');
          retinACanvases.forEach(canvas => new RetinASparkles(canvas));
        }, 1250);

      }, 300);
    }
  }
}
</script>

<style>
.retin-a-sparkles {
    position: absolute;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 6;
    top: 0;
    left: 0;
}

.sparkles-canvas {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
}

.sparkle {
    position: absolute;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: linear-gradient(45deg, #87CEEB, #00BFFF);
    opacity: 0;
    animation: sparkleAnimation 2s ease-in-out forwards;
}

.sparkle-1 {
    top: 20%;
    left: 20%;
    animation-delay: 0s;
}

.sparkle-2 {
    top: 60%;
    left: 40%;
    animation-delay: 0.5s;
}

.sparkle-3 {
    top: 30%;
    left: 70%;
    animation-delay: 1s;
}

.sparkle-4 {
    top: 70%;
    left: 80%;
    animation-delay: 1.5s;
}

@keyframes sparkleAnimation {
    0% {
        transform: scale(0) rotate(0deg);
        opacity: 0;
    }

    50% {
        transform: scale(1) rotate(180deg);
        opacity: 1;
    }

    100% {
        transform: scale(0) rotate(360deg);
        opacity: 0;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/AcneAnimations.vue